/**
 * Cask & Crystal — bag drawer gift note.
 *
 * - Ticking "Add a handwritten gift note" sets the cart attribute
 *   `Gift order: Yes` (the same attribute the PDP gift panel uses) and opens
 *   the note field. Unticking clears both the attribute and the note.
 * - The note saves to `cart.note` 400 ms after typing stops, and immediately
 *   when the field loses focus.
 * - Both controls also carry `form="cart-form"`, so if the shopper hits
 *   checkout before a save lands, the form submission carries them anyway.
 *
 * Listeners are delegated from `document`, so they survive the drawer being
 * morphed on every cart change. No dependencies; loaded as a module only when
 * the bag has items.
 */
const TOGGLE = '[data-ccb-gift-toggle]';
const NOTE = '[data-ccb-note]';
const WRAP = '[data-ccb-gift]';

/** What the shopper last asked for; re-applied if a stale re-render overwrote it. */
const want = { gift: null, note: null };
let timer = 0;
let pending = 0;

function endpoint() {
  const base = (window.Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update';
  return `${base}.js`;
}

async function save(body) {
  pending += 1;
  try {
    await fetch(endpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (error) {
    console.warn('[cc-bag] gift note not saved', error);
  } finally {
    pending -= 1;
    if (pending === 0) reconcile();
  }
}

function reconcile() {
  const toggle = document.querySelector(TOGGLE);
  const note = document.querySelector(NOTE);
  if (toggle && want.gift !== null && toggle.checked !== want.gift) {
    toggle.checked = want.gift;
    setOpen(toggle, want.gift);
  }
  if (note && want.note !== null && note.value !== want.note && document.activeElement !== note) {
    note.value = want.note;
  }
}

function setOpen(toggle, open) {
  const wrap = toggle.closest(WRAP);
  if (wrap) wrap.toggleAttribute('data-open', open);
}

function saveNote(note) {
  clearTimeout(timer);
  if (want.note === note.value) return;
  want.note = note.value;
  const body = { note: note.value };
  if (note.value.trim()) {
    body.attributes = { 'Gift order': 'Yes' };
    want.gift = true;
  }
  save(body);
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  if (target.matches(TOGGLE)) {
    const on = /** @type {HTMLInputElement} */ (target).checked;
    want.gift = on;
    setOpen(target, on);
    const body = { attributes: { 'Gift order': on ? 'Yes' : '' } };
    if (!on) {
      clearTimeout(timer);
      const note = document.querySelector(NOTE);
      if (note) note.value = '';
      want.note = '';
      body.note = '';
    }
    save(body);
  } else if (target.matches(NOTE)) {
    saveNote(/** @type {HTMLTextAreaElement} */ (target));
  }
});

document.addEventListener('input', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLTextAreaElement) || !target.matches(NOTE)) return;
  clearTimeout(timer);
  timer = window.setTimeout(() => saveNote(target), 400);
});